import palette from 'theme/palette';
import { GROUPS_WITH_DIVIDER_ABOVE, GROUPS_WITH_DIVIDER_BELOW, LABEL_COLOR_MAP, LabelDisabledValues, LabelGroup, LabelName, LabelRowSpan, SHRIMP_HEALTH_SETTING_BY_LABEL, TEXT_TYPE_LABELS, THRESHOLD_VALUES, WATER_QUALITY_THRESHOLDS_MAP } from './constants';
import { EditShrimpHealthRequest, EditWaterVibrioRequest, UpdateLabTableRequest } from 'modules/pond/api/types';
import { ShrimpHealthThreshold, WaterQualityThreshold } from 'modules/notifications/api/types';
import { AGE_RANGE_WATER_QUALITY_PARAMETERS } from 'modules/notifications/constants';

interface ColumnValue {
    label: string;
    group: string;
    value: string | number;
    id: number;
}

export const generateCellKey = (
    group: string,
    label: string,
    id: number
): string => {
    return `${group}_${label}_${id}`;
};

export const isLabelDisabled = (label: string): boolean => {
    return Object.values(LabelDisabledValues)
        .map(v => (v as string).toLowerCase())
        .includes(label.toLowerCase());
};

export const hasLabelRowSpan = (label: string): boolean => {
    return Object.values(LabelRowSpan).includes(label as unknown as LabelRowSpan);
};

export const normalizeLabel = (s?: string) => (s ?? '').toString().trim().toLowerCase();

export const getLabelBackgroundColor = (label?: string): string => {
    const key = normalizeLabel(label);
    return LABEL_COLOR_MAP[key];
};

// NK-272: pH / alkali / nitrite / ammonia / turbidity carry `age_ranges`; the
// value is judged against the band covering the column's shrimp age. No band
// for that age (or no age) means nothing to flag.
export const isValueOutOfThreshold = (
    label: string,
    value: number,
    thresholds: WaterQualityThreshold[],
    shrimpAge?: number
): boolean => {
    const param = WATER_QUALITY_THRESHOLDS_MAP[label];
    if (!param) return false;
    const threshold = thresholds.find(t => t.parameter === param && t.is_active);
    if (!threshold) return false;

    if (AGE_RANGE_WATER_QUALITY_PARAMETERS.includes(param)) {
        // Same as the backend: no bands, no age, or no covering band -> no flag.
        if (shrimpAge === undefined || Number.isNaN(shrimpAge)) return false;
        const band = threshold.age_ranges?.find(r => shrimpAge >= r.age_from && shrimpAge <= r.age_to);
        if (!band) return false;
        return value < band.min || value > band.max;
    }

    if (threshold.min === undefined || threshold.max === undefined) return false;
    return value < threshold.min || value > threshold.max;
};

// NK-273: colour limit of a vibrio cell, from the shrimp-health alert settings.
// A cell is coloured when its value is over the `max` of the band covering the
// column's shrimp age — the same bands as the alert. Green is judged on that
// single day; the users' on/off switches do not apply.
// Returns null when no band covers the age (or there is no age) — the cell
// stays uncoloured, like the alert — and undefined when the row is not a vibrio
// row or the settings are not loaded yet (the fixed rule applies).
export const getVibrioColorLimit = (
    label: string,
    thresholds: ShrimpHealthThreshold[] | undefined,
    shrimpAge?: number
): number | null | undefined => {
    const key = SHRIMP_HEALTH_SETTING_BY_LABEL[label];
    if (!key || !thresholds) return undefined;
    const setting = thresholds.find(t => t.target === key.target && t.color === key.color);
    if (!setting) return undefined;
    if (shrimpAge === undefined || Number.isNaN(shrimpAge)) return null;
    const band = setting.age_ranges.find(r => shrimpAge >= r.age_from && shrimpAge <= r.age_to);
    return band ? band.max : null;
};

// True for the last row of a group that closes a section — that row draws the
// emphasized divider (GROUP_DIVIDER_BORDER) instead of the default hairline.
export const isGroupDividerBottomRow = (group: string, isLastInGroup: boolean): boolean => {
    return isLastInGroup && GROUPS_WITH_DIVIDER_BELOW.includes(group as LabelGroup);
};

// True for a group that opens a section, i.e. one that wants an emphasized
// rule above its first row. The rule itself is drawn by the row above (see
// ProcessedRow.isBeforeDividerGroup), so the two edges of the block come out
// exactly the same thickness.
export const opensDividerGroup = (group: string): boolean => {
    return GROUPS_WITH_DIVIDER_ABOVE.includes(group as LabelGroup);
};

export const hasLabelTypeText = (label: string): boolean => {
    return Object.values(TEXT_TYPE_LABELS).includes(label as LabelName);
};